import { TriangleAlert, X } from "@icons";
import { useAnnounce } from "@src/components/live-announcer";
import { NO_INTERPOLATION_ESCAPE } from "@src/raya-lucaria/i18n/interpolation";
import classNames from "classnames";
import {
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import FieldLabel from "../_shared/field-label";
import FieldMessage from "../_shared/field-message";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface TaggedInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  invalidEmails?: string[];
  label?: string;
  info?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  max?: number;
  maxChips?: number;
  allowedDomains?: string[];
  error?: string;
  defaultBadge?: string;
  name?: string;
  ariaLabel?: string;
  invalidEmailMessage?: string;
  duplicateMessage?: (email: string) => string;
  blockedDomainMessage?: string;
  limitReachedMessage?: (max: number) => string;
}

function normalize(email: string): string {
  return email.trim().toLowerCase();
}

function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

export function TaggedInput({
  value,
  onChange,
  invalidEmails,
  label,
  info,
  placeholder,
  disabled = false,
  required = false,
  max,
  maxChips = 6,
  allowedDomains,
  error,
  defaultBadge,
  name,
  ariaLabel,
  invalidEmailMessage,
  duplicateMessage,
  blockedDomainMessage,
  limitReachedMessage,
}: TaggedInputProps) {
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const chipRemoveRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const pendingChipFocus = useRef<number | null>(null);
  const generatedId = useId();
  const inputId = name ?? generatedId;
  const { t } = useTranslation();
  const announce = useAnnounce();

  const invalidMessage = invalidEmailMessage ?? t("taggedInputInvalidEmail");
  const blockedMessage = blockedDomainMessage ?? t("taggedInputBlockedDomain");
  const duplicateText = (email: string) =>
    duplicateMessage
      ? duplicateMessage(email)
      : t("taggedInputDuplicate", {
          email,
          interpolation: NO_INTERPOLATION_ESCAPE,
        });
  const limitText = (limit: number) =>
    limitReachedMessage
      ? limitReachedMessage(limit)
      : t("taggedInputLimit", { max: limit });
  const countText = (count: number) => {
    if (count === 0) return t("taggedInputCountNone");
    if (count === 1) return t("taggedInputCountOne");
    return t("taggedInputCountMany", { count });
  };

  const invalidSet = useMemo(
    () => new Set((invalidEmails ?? []).map(normalize)),
    [invalidEmails],
  );

  const atLimit = max != null && value.length >= max;
  const displayError = draftError ?? error ?? null;
  const hasError = !!displayError;

  const visibleChips = expanded ? value : value.slice(0, maxChips);
  const hiddenCount = value.length - visibleChips.length;

  useEffect(() => {
    const index = pendingChipFocus.current;
    if (index === null) return;
    pendingChipFocus.current = null;
    const visibleCount = expanded
      ? value.length
      : Math.min(maxChips, value.length);
    const target = Math.min(index, visibleCount - 1);
    const chipRemove = target >= 0 ? chipRemoveRefs.current[target] : null;
    (chipRemove ?? inputRef.current)?.focus();
  }, [value, expanded, maxChips]);

  function flashReject() {
    setShake(true);
    window.setTimeout(() => setShake(false), 340);
  }

  function commit(raw: string, focusAtLimit = false): boolean {
    const candidate = normalize(raw.replace(/,+$/, ""));
    if (!candidate) return true;
    if (!isValidEmail(candidate)) {
      setDraftError(invalidMessage);
      flashReject();
      return false;
    }
    if (value.some((email) => normalize(email) === candidate)) {
      setDraftError(duplicateText(candidate));
      flashReject();
      return false;
    }
    if (
      allowedDomains &&
      !allowedDomains.some((domain) =>
        candidate.endsWith(`@${domain.toLowerCase()}`),
      )
    ) {
      setDraftError(blockedMessage);
      flashReject();
      return false;
    }
    if (atLimit) {
      setDraftError(limitText(max as number));
      flashReject();
      return false;
    }
    onChange([...value, candidate]);
    announce(
      t("taggedInputAdded", {
        email: candidate,
        total: countText(value.length + 1),
        interpolation: NO_INTERPOLATION_ESCAPE,
      }),
    );
    if (focusAtLimit && max != null && value.length + 1 >= max) {
      pendingChipFocus.current = value.length;
    }
    setDraft("");
    setDraftError(null);
    return true;
  }

  function removeAt(index: number) {
    onChange(value.filter((_, i) => i !== index));
    setDraftError(null);
    announce(
      t("taggedInputRemoved", {
        email: value[index],
        total: countText(value.length - 1),
        interpolation: NO_INTERPOLATION_ESCAPE,
      }),
    );
  }

  function removeChipAt(index: number) {
    pendingChipFocus.current = index;
    removeAt(index);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit(draft, true);
      return;
    }
    if (event.key === "Backspace" && draft === "" && value.length > 0) {
      removeAt(value.length - 1);
    }
  }

  function handleBlur() {
    if (draft.trim()) commit(draft);
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData("text");
    const tokens = pasted
      .split(/[\s,;]+/)
      .map((token) => normalize(token))
      .filter(Boolean);
    if (tokens.length <= 1) return;

    event.preventDefault();
    const additions: string[] = [];
    let rejected: string | null = null;
    for (const token of tokens) {
      if (max != null && value.length + additions.length >= max) {
        rejected = limitText(max);
        break;
      }
      if (!isValidEmail(token)) {
        rejected = invalidMessage;
        continue;
      }
      if (
        allowedDomains &&
        !allowedDomains.some((domain) =>
          token.endsWith(`@${domain.toLowerCase()}`),
        )
      ) {
        rejected = blockedMessage;
        continue;
      }
      if (!value.includes(token) && !additions.includes(token))
        additions.push(token);
    }
    if (additions.length > 0) {
      onChange([...value, ...additions]);
      const total = countText(value.length + additions.length);
      if (additions.length === 1) {
        announce(
          t("taggedInputAdded", {
            email: additions[0],
            total,
            interpolation: NO_INTERPOLATION_ESCAPE,
          }),
        );
      } else if (value.length === 0) {
        announce(t("taggedInputAddedAll", { count: additions.length }));
      } else {
        announce(t("taggedInputAddedMany", { count: additions.length, total }));
      }
      if (max != null && value.length + additions.length >= max) {
        pendingChipFocus.current = value.length + additions.length - 1;
      }
    }
    setDraft("");
    setDraftError(rejected);
    if (rejected) flashReject();
  }

  return (
    <div className="font-sans">
      <FieldLabel
        htmlFor={inputId}
        label={label}
        required={required}
        info={info}
      />

      <div
        role="group"
        aria-label={ariaLabel ?? label}
        onClick={() => {
          if (!disabled) inputRef.current?.focus();
        }}
        className={classNames(
          "flex flex-wrap items-center gap-1.5 min-h-target px-2 rounded border bg-page transition-[border-color,box-shadow]",
          {
            "border-default cursor-text": !hasError && !disabled,
            "focus-within:border-focus focus-within:ring-2 focus-within:ring-focus":
              !hasError && !disabled,
            "border-danger ring-2 ring-danger": hasError,
            "cursor-not-allowed": disabled,
            "border-default": disabled && !hasError,
            "motion-safe:animate-shake": shake,
          },
        )}
      >
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type="email"
          value={draft}
          placeholder={placeholder ?? t("taggedInputPlaceholder")}
          disabled={disabled || atLimit}
          aria-invalid={hasError}
          className="flex-1 min-w-[110px] h-7 px-1 border-0 outline-none bg-transparent text-sm text-default placeholder:text-muted disabled:cursor-not-allowed"
          onChange={(event) => {
            setDraft(event.target.value);
            if (draftError) setDraftError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
        />
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-0.5">
          <div
            role="list"
            aria-label={t("taggedInputListName", {
              label: ariaLabel ?? label ?? t("taggedInputRecipients"),
              total: countText(value.length),
              interpolation: NO_INTERPOLATION_ESCAPE,
            })}
            className="contents"
          >
            {visibleChips.map((email, index) => {
              const invalid = invalidSet.has(normalize(email));
              return (
                <div
                  role="listitem"
                  key={`${email}-${index}`}
                  data-testid="tagged-input-chip"
                  className={classNames(
                    "my-[9px] inline-flex items-center gap-1.5 h-[26px] pl-2.5 pr-1 rounded-full text-[0.8125rem] font-medium max-w-full",
                    {
                      "bg-tint text-primary": !invalid && !disabled,
                      "bg-danger-subtle text-danger": invalid && !disabled,
                      "bg-raised text-default": disabled,
                    },
                  )}
                >
                  {invalid && (
                    <span className="flex shrink-0 -ml-[3px] text-danger">
                      <TriangleAlert size={13} />
                    </span>
                  )}
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                    {email}
                  </span>
                  {!disabled && (
                    <button
                      ref={(element) => {
                        chipRemoveRefs.current[index] = element;
                      }}
                      type="button"
                      aria-label={t(
                        invalid
                          ? "taggedInputRemoveInvalid"
                          : "taggedInputRemove",
                        { email, interpolation: NO_INTERPOLATION_ESCAPE },
                      )}
                      onClick={(event) => {
                        event.stopPropagation();
                        removeChipAt(index);
                      }}
                      onKeyDown={(event) => {
                        if (
                          event.key === "Delete" ||
                          event.key === "Backspace"
                        ) {
                          event.preventDefault();
                          removeChipAt(index);
                        }
                      }}
                      className={classNames(
                        "relative hit-target inline-flex items-center justify-center w-[18px] h-[18px] shrink-0 rounded-full border-0 bg-transparent cursor-pointer transition-colors",
                        {
                          "text-primary hover:bg-tint hover:text-primary":
                            !invalid,
                          "text-danger hover:bg-danger-subtle": invalid,
                        },
                      )}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {value.length > maxChips && (
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((open) => !open)}
              className="relative hit-target inline-flex items-center h-[26px] px-2.5 rounded-full border border-dashed border-default bg-transparent text-xs font-medium text-muted cursor-pointer transition-colors hover:bg-page hover:text-default hover:border-default"
            >
              {expanded
                ? t("taggedInputShowLess")
                : t("taggedInputMore", { count: hiddenCount })}
            </button>
          )}
        </div>
      )}

      <FieldMessage
        error={displayError}
        defaultBadge={defaultBadge}
        isEmpty={value.length === 0}
      />
    </div>
  );
}

export default TaggedInput;
